<template>
  <div class="d2-multiple-page-control-group" flex>
    <div class="d2-multiple-page-control-content" flex-box="1">
      <div class="d2-multiple-page-control-content-inner">
        <d2-contextmenu
          :visible.sync="contextmenuFlag"
          :x="contentmenuX"
          :y="contentmenuY">
          <d2-contextmenu-list
            :menulist="tagName === '/index' ? contextmenuListIndex : contextmenuList"
            @rowClick="contextmenuClick"/>
        </d2-contextmenu>
        <el-tabs
          ref="pageTabs"
          class="d2-multiple-page-control"
          :value="current"
          type="card"
          :closable="true"
          @tab-click="handleClick"
          @edit="handleTabsEdit"
          @contextmenu.native="handleContextmenu"
          @dragstart.native="handleDragStart"
          @dragover.native="handleDragOver"
          @drop.native="handleDrop"
          @dragend.native="handleDragEnd">
          <el-tab-pane
            v-for="page in opened"
            :key="page.fullPath"
            :label="page.meta.title || '未命名'"
            :name="page.fullPath"/>
        </el-tabs>
      </div>
    </div>
    <!-- 顶部对齐避免退出按钮被 flex 拉高 1px，底边才能与关闭按钮保持连续。 -->
    <div
      class="d2-multiple-page-control-btn"
      :class="{ 'is-fullscreen': fullscreenActive }"
      flex="cross:top"
      flex-box="0">
      <!-- 顶栏隐藏后，退出入口紧邻关闭页签按钮，始终可以恢复导航。 -->
      <d2-header-fullscreen v-if="fullscreenActive" tab-control />
      <el-dropdown
        size="default"
        split-button
        @click="closeAll"
        @command="command => handleControlItemClick(command)">
        <d2-icon name="times-circle"/>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item command="left">
            <d2-icon name="arrow-left" class="d2-mr-10"/>
            关闭左侧
          </el-dropdown-item>
          <el-dropdown-item command="right">
            <d2-icon name="arrow-right" class="d2-mr-10"/>
            关闭右侧
          </el-dropdown-item>
          <el-dropdown-item command="other">
            <d2-icon name="times" class="d2-mr-10"/>
            关闭其它
          </el-dropdown-item>
          <el-dropdown-item command="all">
            <d2-icon name="times-circle" class="d2-mr-10"/>
            全部关闭
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import D2HeaderFullscreen from '../header-fullscreen'
export default {
  components: {
    D2HeaderFullscreen,
    D2Contextmenu: () => import('../contextmenu'),
    D2ContextmenuList: () => import('../contextmenu/components/contentmenuList')
  },
  data () {
    return {
      contextmenuFlag: false,
      contentmenuX: 0,
      contentmenuY: 0,
      contextmenuListIndex: [
        { icon: 'times-circle', title: '关闭全部', value: 'all' }
      ],
      contextmenuList: [
        { icon: 'arrow-left', title: '关闭左侧', value: 'left' },
        { icon: 'arrow-right', title: '关闭右侧', value: 'right' },
        { icon: 'times', title: '关闭其它', value: 'other' },
        { icon: 'times-circle', title: '关闭全部', value: 'all' }
      ],
      tagName: '/index',
      dragFullPath: '',
      suppressTabClick: false
    }
  },
  computed: {
    ...mapState('d2admin/fullscreen', {
      // 紧凑布局下保留页签，并显示退出布局的入口。
      fullscreenActive: state => state.active
    }),
    ...mapState('d2admin/page', [
      'opened',
      'current'
    ])
  },
  mounted () {
    // Element UI 的页签标题由子组件渲染，只能在挂载后标记可拖动节点。
    this.syncTabDraggable()
  },
  updated () {
    // 父组件 updated 晚于 Element UI 子页签更新，可覆盖刷新恢复和最新新增的页签。
    this.syncTabDraggable()
  },
  methods: {
    ...mapActions('d2admin/page', [
      'close',
      'closeLeft',
      'closeRight',
      'closeOther',
      'closeAll',
      'reorder'
    ]),
    /**
     * @description 为 Element UI 实际页签节点设置拖动能力，首页不可拖动
     */
    syncTabDraggable () {
      const tabs = this.$refs.pageTabs
      if (!tabs) return
      const tabElements = tabs.$el.querySelectorAll('.el-tabs__item')
      Array.prototype.forEach.call(tabElements, tabElement => {
        const fullPath = this.getTabFullPath(tabElement)
        const draggable = fullPath && fullPath !== '/index'
        tabElement.setAttribute('draggable', draggable ? 'true' : 'false')
        tabElement.classList.toggle('is-d2-tab-draggable', Boolean(draggable))
      })
    },
    /**
     * @description 从事件源向上查找页签节点，避免标题或关闭图标成为事件源时识别失败
     */
    getTabElement (target) {
      const tabs = this.$refs.pageTabs
      const root = tabs && tabs.$el
      while (target && target !== root) {
        if (target.classList && target.classList.contains('el-tabs__item')) return target
        target = target.parentNode
      }
      return null
    },
    /**
     * @description 从 Element UI 页签的 aria-controls 中还原页面 fullPath
     */
    getTabFullPath (tabElement) {
      const controls = tabElement && tabElement.getAttribute('aria-controls')
      return controls && controls.indexOf('pane-') === 0 ? controls.slice(5) : ''
    },
    /**
     * @description 清理拖动过程中的透明度和目标位置提示
     */
    clearDragStyles () {
      const tabs = this.$refs.pageTabs
      if (!tabs) return
      const tabElements = tabs.$el.querySelectorAll('.el-tabs__item')
      Array.prototype.forEach.call(tabElements, tabElement => {
        tabElement.classList.remove('is-d2-tab-dragging', 'is-d2-tab-drop-before', 'is-d2-tab-drop-after')
      })
    },
    /**
     * @description 开始拖动普通页签，并阻止拖动结束时误触切换页面
     */
    handleDragStart (event) {
      const tabElement = this.getTabElement(event.target)
      const fullPath = this.getTabFullPath(tabElement)
      if (!fullPath || fullPath === '/index') {
        event.preventDefault()
        return
      }
      this.dragFullPath = fullPath
      this.suppressTabClick = true
      tabElement.classList.add('is-d2-tab-dragging')
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('text/plain', fullPath)
      }
    },
    /**
     * @description 根据鼠标位于目标页签的左右半区显示插入位置
     */
    handleDragOver (event) {
      if (!this.dragFullPath) return
      const tabElement = this.getTabElement(event.target)
      const dropFullPath = this.getTabFullPath(tabElement)
      if (!dropFullPath || dropFullPath === this.dragFullPath) return
      const rect = tabElement.getBoundingClientRect()
      const position = event.clientX < rect.left + rect.width / 2 ? 'before' : 'after'
      // 首页只能作为“插入到首页之后”的目标，保证首页固定在最左侧。
      if (dropFullPath === '/index' && position === 'before') return
      event.preventDefault()
      this.clearDragStyles()
      const dragElement = Array.prototype.find.call(
        this.$refs.pageTabs.$el.querySelectorAll('.el-tabs__item'),
        item => this.getTabFullPath(item) === this.dragFullPath
      )
      if (dragElement) dragElement.classList.add('is-d2-tab-dragging')
      tabElement.classList.add(`is-d2-tab-drop-${position}`)
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
    },
    /**
     * @description 在目标位置完成页签排序，Vuex 负责校验并持久化顺序
     */
    handleDrop (event) {
      if (!this.dragFullPath) return
      const tabElement = this.getTabElement(event.target)
      const dropFullPath = this.getTabFullPath(tabElement)
      if (!dropFullPath || dropFullPath === this.dragFullPath) return
      const rect = tabElement.getBoundingClientRect()
      const position = event.clientX < rect.left + rect.width / 2 ? 'before' : 'after'
      if (dropFullPath === '/index' && position === 'before') return
      event.preventDefault()
      this.reorder({
        dragFullPath: this.dragFullPath,
        dropFullPath,
        position
      })
    },
    /**
     * @description 结束拖动并延迟恢复点击，避免浏览器在 mouseup 后打开被拖页签
     */
    handleDragEnd () {
      this.dragFullPath = ''
      this.clearDragStyles()
      setTimeout(() => {
        this.suppressTabClick = false
      }, 0)
    },
    /**
     * @description 右键菜单功能点击
     */
    handleContextmenu (event) {
      let target = event.target
      // 解决 https://github.com/d2-projects/d2-admin/issues/54
      let flag = false
      if (target.className.indexOf('el-tabs__item') > -1) flag = true
      else if (target.parentNode.className.indexOf('el-tabs__item') > -1) {
        target = target.parentNode
        flag = true
      }
      if (flag) {
        event.preventDefault()
        event.stopPropagation()
        this.contentmenuX = event.clientX
        this.contentmenuY = event.clientY
        this.tagName = target.getAttribute('aria-controls').slice(5)
        this.contextmenuFlag = true
      }
    },
    /**
     * @description 右键菜单的row-click事件
     */
    contextmenuClick (command) {
      this.handleControlItemClick(command, this.tagName)
    },
    /**
     * @description 接收点击关闭控制上选项的事件
     */
    handleControlItemClick (command, tagName = null) {
      if (tagName) {
        this.contextmenuFlag = false
      }
      const params = {
        pageSelect: tagName
      }
      switch (command) {
        case 'left':
          this.closeLeft(params)
          break
        case 'right':
          this.closeRight(params)
          break
        case 'other':
          this.closeOther(params)
          break
        case 'all':
          this.closeAll()
          break
        default:
          this.$message.error('无效的操作')
          break
      }
    },
    /**
     * @description 接收点击 tab 标签的事件
     */
    handleClick (tab, event) {
      if (this.suppressTabClick) return
      // 找到点击的页面在 tag 列表里是哪个
      const page = this.opened.find(page => page.fullPath === tab.name)
      if (page) {
        const { name, params, query } = page
        this.$router.push({ name, params, query })
      }
    },
    /**
     * @description 点击 tab 上的删除按钮触发这里 首页的删除按钮已经隐藏 因此这里不用判断是 index
     */
    handleTabsEdit (tagName, action) {
      if (action === 'remove') {
        this.close({
          tagName
        })
      }
    }
  }
}
</script>

<style scoped>
.d2-multiple-page-control >>> .el-tabs__item.is-d2-tab-draggable {
  cursor: grab;
}

.d2-multiple-page-control >>> .el-tabs__item.is-d2-tab-dragging {
  opacity: .5;
  cursor: grabbing;
}

.d2-multiple-page-control >>> .el-tabs__item.is-d2-tab-drop-before {
  box-shadow: inset 3px 0 #409eff;
}

.d2-multiple-page-control >>> .el-tabs__item.is-d2-tab-drop-after {
  box-shadow: inset -3px 0 #409eff;
}
</style>

<vue-filename-injector>
export default function (Component) {
  Component.options.__source = "src/layout/header-aside/components/tabs/index.vue"
}
</vue-filename-injector>
